/**
 * Fetch helper that retries ONCE on 401 after refreshing the auth token.
 *
 * Motivation: the support-chat widget holds a JWT that expires. A background
 * timer refreshes it every ~25 min, but if a refresh is ever delayed (network
 * blip, Inkeep API hiccup) the in-flight fetch hits 401 and — with the
 * original empty `catch {}` pattern — the UI silently loops forever with a
 * stale token. `fetchWithAuthRetry` surfaces that state: on 401 it calls the
 * caller's refresh hook, rebuilds the request with the fresh token, and
 * retries exactly once before giving up.
 */

type FetchImpl = typeof fetch;

interface RetryOptions {
  /** Called when the first attempt returns 401. Must resolve with a fresh token. */
  onAuthFailure: () => Promise<string>;
  /** Injectable fetch implementation (tests use a stub). Defaults to global `fetch`. */
  fetchImpl?: FetchImpl;
}

/**
 * @param buildRequest - Given the current token (or `undefined` before any refresh),
 *   return the URL + `RequestInit` for the call. Called up to twice: first with the
 *   initial token, then after a 401 with the refreshed token.
 * @param initialToken - Token to use on the first attempt; passed to `buildRequest`.
 */
export async function fetchWithAuthRetry(
  buildRequest: (token: string | undefined) => { url: string; init: RequestInit },
  initialToken: string | undefined,
  opts: RetryOptions,
): Promise<Response> {
  const fetchImpl = opts.fetchImpl ?? fetch;

  const first = buildRequest(initialToken);
  const firstResp = await fetchImpl(first.url, first.init);
  if (firstResp.status !== 401) return firstResp;

  const freshToken = await opts.onAuthFailure();
  const second = buildRequest(freshToken);
  return fetchImpl(second.url, second.init);
}

/** Classification used by the UI to decide whether to retry, show an error, etc. */
type PollFailureClass = 'auth' | 'timeout' | 'network' | 'server' | 'unknown';

export function classifyFetchFailure(err: unknown, response?: Response): PollFailureClass {
  if (response) {
    if (response.status === 401 || response.status === 403) return 'auth';
    if (response.status >= 500) return 'server';
  }
  if (err instanceof Error) {
    const m = err.message.toLowerCase();
    if (err.name === 'AbortError' || m.includes('timeout')) return 'timeout';
    if (
      m.includes('failed to fetch') ||
      m.includes('networkerror') ||
      m.includes('network request failed')
    ) {
      return 'network';
    }
  }
  return 'unknown';
}
